import { z } from 'zod';
import type { ItemTipo } from './itens.js';
import { filtrosMargens } from './margens.js';
import type { FaixaMargem } from './margens.js';
import type { StatusOrcamentoDto } from './orcamentos.js';

/**
 * Custo "na epoca" x "hoje" (Sessao 5, E12). Tudo e calculado no servidor com o motor; o front so formata.
 *
 * Regra (docs/SESSAO5-DESENHO.md b): so proposta ENVIADO/EM_NEGOCIACAO do app, DENTRO da validade da versao de referencia
 * (ENVIADA), tem comparativo. Rascunho e sempre ao vivo; Vencido, Aprovado, Perdido e Importado: so o snapshot, e o campo
 * `comparativoCusto` (e `alertaCusto`) NEM EXISTE na resposta.
 */

/** Comparativo entre o lucro EXIBIDO no envio (snapshot) e o lucro de hoje (custos atuais, mesmos precos do snapshot). */
export interface ComparativoCustoResponse {
  /** `queda > limiarAlertaLucro` (estrito). Quando verdadeiro o front mostra o chip e o icone. */
  acimaDoLimiar: boolean;
  /** Faixa (cor) do lucro no envio e hoje (a do motor, `profitBand`, sobre o lucro SEM frete: ver README P6). */
  faixaNoEnvio: FaixaMargem | null;
  faixaHoje: FaixaMargem | null;
  /** `faixaNoEnvio !== faixaHoje`. E o unico dado que o VENDEDOR recebe, junto do aviso. */
  mudouDeCor: boolean;
  /**
   * So com `custo.ver` (a chave NAO existe para o VENDEDOR e demais): lucro exibido no envio e hoje (fracao, 4 casas),
   * queda e limiar em PONTOS PERCENTUAIS (2 casas: `"2.50"` = 2,5 pp), itens sem custo hoje e o dia da conta.
   */
  noEnvio?: string;
  hoje?: string;
  quedaPp?: string;
  limiarPp?: string | null;
  /** Lucro e queda em precisao cheia (fracao, 8 casas), para conferencia. */
  lucroNoEnvio?: string;
  lucroHoje?: string;
  queda?: string;
  /** Itens que nao tem custo hoje (arquivado, sem ficha, CPM ausente, ciclo): mantem o custo do snapshot. */
  itensSemCustoHoje?: number;
  /** O lucro comparado e o EXIBIDO: com frete quando `consideraFreteLucro` (DP4). */
  consideraFreteLucro?: boolean;
  calculadoEm?: string;
}

/** Linha do bloco "Propostas abertas com queda de lucro alem do limiar" do painel de margens. */
export interface MargensPropostaQuedaLinha {
  orcamentoId: string;
  numero: number;
  numeroExibido: string;
  status: StatusOrcamentoDto;
  clienteId: string | null;
  clienteNome: string | null;
  tabelaId: string;
  tabelaNome: string;
  /** Data do orcamento (versao de referencia). */
  data: string;
  validaAte: string;
  faixaNoEnvio: FaixaMargem | null;
  faixaHoje: FaixaMargem | null;
  itensSemCustoHoje: number;
  consideraFreteLucro: boolean;
  exibicao: { receita: string; noEnvio: string; hoje: string; quedaPp: string; limiarPp: string };
}

export const margensCustoAtualizadoPropostasSchema = z.object({
  ...filtrosMargens,
  ordenar: z.enum(['queda', 'numero', 'validade']).default('queda'),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type MargensCustoAtualizadoPropostasQuery = z.infer<typeof margensCustoAtualizadoPropostasSchema>;

export interface MargensCustoAtualizadoPropostasResponse {
  itens: MargensPropostaQuedaLinha[];
  /** Propostas com queda acima do limiar (apos filtros), independente de limit/offset. */
  total: number;
  limit: number;
  offset: number;
  /** Limiar da vigencia de parametros de hoje, em pontos percentuais; null sem parametros vigentes (nenhum alerta). */
  limiarPp: string | null;
  /** Propostas abertas na validade que passaram nos filtros e foram calculadas (ate `maximoCalculado`). */
  calculadas: number;
  /** Quantas ficaram de fora do calculo por passar de `maximoCalculado` (as mais recentes entram primeiro). */
  foraDoLimite: number;
  maximoCalculado: number;
  calculadoEm: string;
}

export const margensCustoAtualizadoPraticadosSchema = z.object({
  ...filtrosMargens,
  ordenar: z.enum(['delta', 'codigo', 'desde']).default('delta'),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type MargensCustoAtualizadoPraticadosQuery = z.infer<typeof margensCustoAtualizadoPraticadosSchema>;

/** Linha do bloco "Praticados que perderam margem desde que foram definidos": item x tabela x condicao com marca aberta. */
export interface MargensPraticadoPerdeuLinha {
  itemId: string;
  itemCodigo: string;
  itemDescricao: string;
  tipo: ItemTipo;
  tabelaId: string;
  tabelaCodigo: string;
  tabelaNome: string;
  condicaoId: string;
  condicaoCodigo: string;
  /** Origem da marca: lote de custo aplicado ou troca de ficha escolhida. */
  origem: 'LOTE' | 'FICHA_ESCOLHIDA';
  desde: string;
  /** Praticado atual e sugerido quando o praticado foi definido / hoje (ao vivo), 2 casas. */
  praticado: string | null;
  sugeridoAntes: string;
  sugeridoHoje: string;
  delta: string;
  deltaPct: string | null;
  /** Lucro do praticado hoje (fracao, 4 casas), meta e minimo ja resolvidos (tabela > empresa). Nulos se nao ha custo/praticado hoje. */
  lucroHoje: string | null;
  metaLucro: string | null;
  minimoLucro: string | null;
  abaixoDaMeta: boolean | null;
  abaixoDoMinimo: boolean | null;
}

export interface MargensCustoAtualizadoPraticadosResponse {
  itens: MargensPraticadoPerdeuLinha[];
  /** Marcas abertas (apos filtros), independente de limit/offset. */
  total: number;
  limit: number;
  offset: number;
  calculadoEm: string;
}
